import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import { supabase } from '@/lib/supabase';
import { useLevels, activeLevelNames } from '@/hooks/useLevels';

interface LessonInfo {
  id: string;
  title: string;
  group_id: string | null;
  level: string | null;
}

interface GroupInfo {
  id: string;
  name: string;
  color: string | null;
  level: string | null;
}

interface DocRow {
  id: string;
  title: string;
  description: string | null;
  type: string | null;
  lesson_id: string | null;
  group_id: string | null;
  level: string | null;
  published: boolean;
  downloadable: boolean;
  size: string | null;
  file_url: string | null;
}

interface DocumentModalProps {
  open: boolean;
  onClose: () => void;
  doc: DocRow | null;
  lessons: LessonInfo[];
  groups: GroupInfo[];
}

export default function DocumentModal({ open, onClose, doc, lessons, groups }: DocumentModalProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [lessonId, setLessonId] = useState('');
  const [manualGroupId, setManualGroupId] = useState('');
  const [manualLevel, setManualLevel] = useState('');
  const { levels } = useLevels();
  const levelOptions = activeLevelNames(levels);

  useEffect(() => {
    if (open) {
      setLessonId(doc?.lesson_id ?? '');
      setManualGroupId(doc?.group_id ?? '');
      setManualLevel(doc?.level ?? '');
    } else {
      setError(null);
      setSaving(false);
      setSelectedFile(null);
    }
  }, [open, doc]);

  const selectedLesson = lessons.find((l) => l.id === lessonId);
  const autoGroupId = selectedLesson?.group_id ?? null;
  const autoLevel = selectedLesson?.level ?? null;
  const groupName = groups.find((g) => g.id === autoGroupId)?.name;

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const title = (fd.get('title') as string || '').trim();
    const description = (fd.get('description') as string || '').trim() || null;
    const type = (fd.get('type') as string || '').trim() || 'diger';
    const file_url = (fd.get('file_url') as string || '').trim() || null;
    const published = fd.get('published') === 'on';
    const downloadable = fd.get('downloadable') === 'on';
    const file = selectedFile;

    const lesson_id = lessonId || null;
    const group_id = lessonId ? autoGroupId : manualGroupId || null;
    const level = lessonId ? autoLevel : manualLevel || null;

    if (!title) {
      setError('Lütfen doküman başlığı girin.');
      return;
    }
    if (!lesson_id && !group_id) {
      setError('Doküman bir derse bağlı değilse grup seçimi zorunludur.');
      return;
    }

    setError(null);
    setSaving(true);

    try {
      let docId: string | null = doc?.id ?? null;

      if (doc) {
        const { error: updateError } = await supabase
          .from('zehrahoca_documents')
          .update({
            title,
            description,
            type,
            lesson_id,
            group_id,
            level,
            file_url,
            published,
            downloadable,
          })
          .eq('id', doc.id);
        if (updateError) throw updateError;
      } else {
        const { data: inserted, error: insertError } = await supabase
          .from('zehrahoca_documents')
          .insert({
            title,
            description,
            type,
            lesson_id,
            group_id,
            level,
            file_url,
            published,
            downloadable,
            upload_date: new Date().toISOString().slice(0, 10),
          })
          .select('id')
          .single();
        if (insertError) throw insertError;
        docId = inserted.id;
      }

      if (file && file.size > 0 && docId) {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '-').toLowerCase();
        const path = `docs/${docId}/${safeName}`;
        const { error: uploadError } = await supabase.storage
          .from('documents')
          .upload(path, file, { upsert: true });
        if (uploadError) throw new Error(`Dosya yüklenemedi: ${uploadError.message}`);
        const { data: publicUrl } = supabase.storage.from('documents').getPublicUrl(path);
        await supabase
          .from('zehrahoca_documents')
          .update({
            file_url: publicUrl.publicUrl,
            size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
          })
          .eq('id', docId);
      }

      form.reset();
      setSelectedFile(null);
      window.dispatchEvent(new Event('zehrahoca:docs-changed'));
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Doküman kaydedilirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={doc ? 'Doküman Düzenle' : 'Yeni Doküman Ekle'}
      subtitle={doc ? 'Doküman bilgilerini güncelleyin.' : 'Yeni bir doküman oluşturun ve paylaşın.'}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Başlık</label>
            <input
              type="text"
              name="title"
              defaultValue={doc?.title ?? ''}
              required
              placeholder="Örn: Türev Konusu Özet Notları"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Tip</label>
            <select
              name="type"
              defaultValue={doc?.type ?? 'pdf'}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="pdf">PDF</option>
              <option value="video">Video</option>
              <option value="sunum">Sunum</option>
              <option value="word">Word</option>
              <option value="excel">Excel</option>
              <option value="diger">Diğer</option>
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Bağlı Ders (opsiyonel)</label>
            <select
              value={lessonId}
              onChange={(e) => setLessonId(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="">Ders seçin</option>
              {lessons.map((l) => (
                <option key={l.id} value={l.id}>{l.title}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
              Grup {!lessonId && <span className="text-red-500">*</span>}
            </label>
            {lessonId ? (
              <input
                type="text"
                value={groupName ?? 'Dersin grubu yok'}
                disabled
                className="w-full cursor-not-allowed rounded-lg border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-500"
              />
            ) : (
              <select
                value={manualGroupId}
                onChange={(e) => setManualGroupId(e.target.value)}
                className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
              >
                <option value="">Grup seçin</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Seviye</label>
            {lessonId ? (
              <input
                type="text"
                value={autoLevel ?? 'Dersin seviyesi yok'}
                disabled
                className="w-full cursor-not-allowed rounded-lg border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-500"
              />
            ) : (
              <select
                value={manualLevel}
                onChange={(e) => setManualLevel(e.target.value)}
                className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
              >
                <option value="">Seçin</option>
                {levelOptions.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            )}
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Dosya Yükle</label>
            <div className="relative flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-background-300 bg-background-50 px-4 py-6 text-sm text-foreground-600 transition hover:border-primary-300 hover:bg-primary-50">
              <i className="ri-upload-cloud-2-line text-xl" />
              {selectedFile ? (
                <span className="text-foreground-800 font-medium">{selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(1)} MB)</span>
              ) : (
                <span>Dosya seçmek için tıklayın (PDF, Word, Excel, Sunum, görsel...)</span>
              )}
              <input
                type="file"
                name="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg"
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Harici Link URL (opsiyonel)</label>
            <input
              type="url"
              name="file_url"
              defaultValue={doc?.file_url ?? ''}
              placeholder="https://drive.google.com/... veya video linki"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Açıklama</label>
            <textarea
              name="description"
              defaultValue={doc?.description ?? ''}
              rows={3}
              maxLength={500}
              placeholder="Doküman hakkında kısa açıklama..."
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
            <p className="mt-1 text-xs text-foreground-400">Maksimum 500 karakter</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-5 rounded-lg bg-background-50 p-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground-800">
            <input
              type="checkbox"
              name="published"
              defaultChecked={doc?.published ?? true}
              className="h-4 w-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
            />
            Yayınla
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground-800">
            <input
              type="checkbox"
              name="downloadable"
              defaultChecked={doc?.downloadable ?? true}
              className="h-4 w-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
            />
            İndirilebilir
          </label>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
            <i className="ri-error-warning-line mt-0.5 text-base" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer whitespace-nowrap"
          >
            {saving ? 'Kaydediliyor...' : doc ? 'Güncelle' : 'Kaydet'}
          </button>
        </div>
      </form>
    </Modal>
  );
}